import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { API } from "../api";
import { getPasswordSelector } from "./authSlice";
import {
  getSelfServiceAddSKUSelector,
  getSelfServiceCnameSelector,
  getSelfServiceConvention,
  selfServiceSlice,
} from "./selfServiceSlice";
import { cloneDeep } from "lodash";

// ACTIONS --------------------------------------------------------------------

/**
 * @desc Get conventions.
 */
export const fetchConventions = createAsyncThunk(
  "conventions/fetch",
  async (_data, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      if (!state || typeof state !== typeof {}) return;
      const token = getPasswordSelector(state);
      const response = await API.Convention.GetAll({ token });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * @desc Create a new convention. Based on form state.
 */
export const createConvention = createAsyncThunk(
  "convention/create",
  async (cname, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      if (!state || typeof state !== typeof {}) return;

      const token = getPasswordSelector(state);
      if (!cname || !token) return;

      const response = await API.Convention.Update({
        params: { name: cname },
        token,
      });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * @desc Get convention products for current brand.
 */
export const fetchConventionProducts = createAsyncThunk(
  "convention/products",
  async (cname, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      if (!state || typeof state !== typeof {}) return;
      const token = getPasswordSelector(state);
      const response = await API.Convention.GetProducts({
        params: {
          name: cname,
        },
        token,
      });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * @desc Update convention for current brand.
 */
export const updateConvention = createAsyncThunk(
  "convention/update",
  async (convention, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      if (!state || typeof state !== typeof {}) return;
      const token = getPasswordSelector(state);
      const response = await API.Convention.Update({
        params: {
          name: convention.name,
        },
        payload: convention,
        token,
      });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * @desc Delete a convention. Based on form state.
 */
export const deleteConvention = createAsyncThunk(
  "convention/delete",
  async (_data, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      if (!state || typeof state !== typeof {}) return;

      const token = getPasswordSelector(state);
      const cname = getSelfServiceCnameSelector(state);

      if (!cname || !token) return;

      const response = await API.Convention.Delete({
        params: {
          name: cname,
        },
        token,
      });
      await dispatch(selfServiceSlice.actions.resetForm());

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * @desc Add a product.
 */
export const addProduct = createAsyncThunk(
  "product/add",
  async (_data, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      if (!state || typeof state !== typeof {}) return;

      const token = getPasswordSelector(state);
      const sku = getSelfServiceAddSKUSelector(state);
      const convention = getSelfServiceConvention(state);
      if (!token || !sku || !convention) return;

      const { pskus = [] } = convention;
      if (pskus.includes(sku)) return;

      // 1. Create Product
      await API.Product.Update({ params: { sku }, token });

      // 2. Add Product to Convention
      await dispatch(
        updateConvention({
          name: convention.name,
          pskus: [...pskus, sku],
        })
      );

      // 3. Fetch convention products.
      await dispatch(fetchConventionProducts(convention.name));

      return;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * @desc Delete a product.
 */
export const deleteProduct = createAsyncThunk(
  "product/delete",
  async ({ sku }, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      if (!state || typeof state !== typeof {}) return;

      const token = getPasswordSelector(state);
      const convention = getSelfServiceConvention(state);
      if (!token || !sku || !convention) return;

      const { pskus = [] } = convention;
      if (!pskus.includes(sku)) return;
      const copy = cloneDeep(pskus);
      const removeIndex = pskus.findIndex((psku) => psku === sku);
      if (removeIndex > -1) {
        copy.splice(removeIndex, 1);
      }

      // 1. Remove Product from Convention
      await dispatch(
        updateConvention({
          name: convention.name,
          pskus: copy,
        })
      );

      // 2. Fetch convention products.
      await dispatch(fetchConventionProducts(convention.name));

      return;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

/**
 * @desc Update a product.
 */
export const updateProduct = createAsyncThunk(
  "product/update",
  async (data, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      if (!state || typeof state !== typeof {}) return;
      const token = getPasswordSelector(state);
      const { product } = data;
      const response = API.Product.Update({
        params: {
          sku: product.sku,
        },
        payload: product,
        token,
      });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// SLICE ----------------------------------------------------------------------

/**
 * @desc Conventions Slice.
 */
export const conventionsSlice = createSlice({
  name: "conventions",
  initialState: {
    pending: false,
    data: [],
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Conventions
      .addCase(fetchConventions.pending, (state) => {
        state.pending = true;
      })
      .addCase(fetchConventions.fulfilled, (state, action) => {
        if (Array.isArray(action.payload)) {
          state.data = action.payload;
        }
        state.pending = false;
      })
      .addCase(fetchConventions.rejected, (state) => {
        state.pending = false;
      })

      // Fetch Products
      .addCase(fetchConventionProducts.pending, (state) => {
        state.pending = true;
      })
      .addCase(fetchConventionProducts.fulfilled, (state, action) => {
        const cname = action.meta.arg;
        const targetIndex = state.data.findIndex(
          (convention) => convention.name === cname
        );
        if (targetIndex > -1) {
          state.data[targetIndex].products = action.payload;
        }
        state.pending = false;
      })
      .addCase(fetchConventionProducts.rejected, (state) => {
        state.pending = false;
      })

      // Update Product
      .addCase(updateProduct.pending, (state) => {
        state.pending = true;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        const { cname } = action.meta.arg;
        const targetIndex = state.data.findIndex(
          (convention) => convention.name === cname
        );
        if (targetIndex > -1) {
          const productIndex = state.data[targetIndex].products.findIndex(
            (product) => product.sku === action.payload.sku
          );
          if (productIndex > -1) {
            state.data[targetIndex].products[productIndex] = action.payload;
          }
        }
        state.pending = false;
      })
      .addCase(updateProduct.rejected, (state) => {
        state.pending = false;
      })

      // Create Conventions
      .addCase(createConvention.pending, (state) => {
        state.pending = true;
      })
      .addCase(createConvention.fulfilled, (state, action) => {
        if (action.payload) {
          state.data.push(action.payload);
        }
        state.pending = false;
      })
      .addCase(createConvention.rejected, (state) => {
        state.pending = false;
      })

      // Update Conventions
      .addCase(updateConvention.pending, (state) => {
        state.pending = true;
      })
      .addCase(updateConvention.fulfilled, (state, action) => {
        const updatedData = action.payload;
        const targetIndex = state.data.findIndex(
          (convention) => convention.name === updatedData.name
        );
        if (targetIndex > -1) {
          state.data[targetIndex] = {
            ...state.data[targetIndex],
            ...updatedData,
          };
        }
        state.pending = false;
      })
      .addCase(updateConvention.rejected, (state) => {
        state.pending = false;
      })

      // Delete Conventions
      .addCase(deleteConvention.pending, (state) => {
        state.pending = true;
      })
      .addCase(deleteConvention.fulfilled, (state, action) => {
        const deleteIndex = state.data.findIndex(
          ({ name }) => name === action.payload
        );
        if (deleteIndex > -1) {
          state.data.splice(deleteIndex, 1);
        }
        state.pending = false;
      })
      .addCase(deleteConvention.rejected, (state) => {
        state.pending = false;
      });
  },
});

// SELECTORS ------------------------------------------------------------------

export const getConventionsSelector = (state) => state.conventions.data || [];

export const getConventionProductsSelector = (cname) => (state) => {
  const convention = getConventionsSelector(state).find(
    (convention) => convention.name === cname
  );
  return convention?.products || [];
};

// REDUCER --------------------------------------------------------------------

export default conventionsSlice.reducer;
